import { Component, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ThemeService } from 'src/app/_services/theme.service';

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
const MAX_SIZE_BYTES = 500 * 1024; // 500 KB

// Recommended maximum dimensions. Anything above these risks layout overflow
// or slow initial paint. Kept permissive enough for retina-grade assets.
const LOGO_MAX_WIDTH = 1000;
const LOGO_MAX_HEIGHT = 400;
const BANNER_MAX_WIDTH = 3000;
const BANNER_MAX_HEIGHT = 1000;
// Banner aspect-ratio guardrails (width / height). Prevents near-square or
// ultra-wide banners that break the header layout.
const BANNER_MIN_RATIO = 2.0;
const BANNER_MAX_RATIO = 8.0;

@Component({
  selector: 'tangram-branding-modal',
  templateUrl: './branding-modal.component.html',
  styleUrls: ['./branding-modal.component.scss'],
})
export class BrandingModalComponent implements OnInit {
  /** Passed in by the caller — the theme ID to update assets on. */
  themeId: string = '';

  logoFile: File | null = null;
  logoPreview: string | null = null;
  logoError: string = '';

  bannerFile: File | null = null;
  bannerPreview: string | null = null;
  bannerError: string = '';

  saving: boolean = false;
  saveError: string = '';

  constructor(
    public activeModal: NgbActiveModal,
    private themeService: ThemeService,
  ) {}

  ngOnInit(): void {}

  // ── File selection ─────────────────────────────────────────────────────────

  onLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files ? input.files[0] : null;
    this.logoError = '';
    if (!file) return;

    const typeErr = this._validateType(file);
    if (typeErr) { this.logoError = typeErr; return; }
    if (file.size > MAX_SIZE_BYTES) {
      this.logoError = 'File must be under 500 KB.';
      return;
    }

    this._readAsDataUrl(file).then(dataUrl => {
      // SVG has no intrinsic raster size — accept as-is.
      if (file.type === 'image/svg+xml') {
        this.logoFile = file;
        this.logoPreview = dataUrl;
        return;
      }
      this._measureImage(dataUrl).then(({ width, height }) => {
        if (width > LOGO_MAX_WIDTH || height > LOGO_MAX_HEIGHT) {
          this.logoError = `Logo is ${width}×${height}. Maximum recommended size is ${LOGO_MAX_WIDTH}×${LOGO_MAX_HEIGHT}.`;
          return;
        }
        this.logoFile = file;
        this.logoPreview = dataUrl;
      }).catch(() => {
        this.logoError = 'Could not read image. Please try another file.';
      });
    });
  }

  onBannerSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files ? input.files[0] : null;
    this.bannerError = '';
    if (!file) return;

    const typeErr = this._validateType(file);
    if (typeErr) { this.bannerError = typeErr; return; }
    if (file.size > MAX_SIZE_BYTES) {
      this.bannerError = 'File must be under 500 KB.';
      return;
    }

    this._readAsDataUrl(file).then(dataUrl => {
      if (file.type === 'image/svg+xml') {
        this.bannerFile = file;
        this.bannerPreview = dataUrl;
        return;
      }
      this._measureImage(dataUrl).then(({ width, height }) => {
        if (width > BANNER_MAX_WIDTH || height > BANNER_MAX_HEIGHT) {
          this.bannerError = `Banner is ${width}×${height}. Maximum recommended size is ${BANNER_MAX_WIDTH}×${BANNER_MAX_HEIGHT}.`;
          return;
        }
        const ratio = width / height;
        if (ratio < BANNER_MIN_RATIO || ratio > BANNER_MAX_RATIO) {
          this.bannerError = `Banner aspect ratio ${ratio.toFixed(2)}:1 is outside the supported range (${BANNER_MIN_RATIO}:1 to ${BANNER_MAX_RATIO}:1).`;
          return;
        }
        this.bannerFile = file;
        this.bannerPreview = dataUrl;
      }).catch(() => {
        this.bannerError = 'Could not read image. Please try another file.';
      });
    });
  }

  private _validateType(file: File): string {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Only PNG, JPG, JPEG or SVG files are allowed.';
    }
    return '';
  }

  private _readAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = e => resolve((e.target as FileReader).result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  private _measureImage(dataUrl: string): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => reject(new Error('image load failed'));
      img.src = dataUrl;
    });
  }

  removeLogo(): void {
    this.logoFile = null;
    this.logoPreview = null;
    this.logoError = '';
  }

  removeBanner(): void {
    this.bannerFile = null;
    this.bannerPreview = null;
    this.bannerError = '';
  }

  // ── Save ──────────────────────────────────────────────────────────────────

  save(): void {
    if (!this.themeId) {
      this.saveError = 'No theme selected. Please create or select a theme first.';
      return;
    }
    if (!this.logoFile && !this.bannerFile) {
      this.activeModal.close(null);
      return;
    }

    this.saving = true;
    this.saveError = '';

    const uploads: Promise<any>[] = [];

    if (this.logoFile) {
      uploads.push(
        this.themeService.uploadThemeAsset(this.themeId, this.logoFile, 'logo').toPromise()
      );
    }
    if (this.bannerFile) {
      uploads.push(
        this.themeService.uploadThemeAsset(this.themeId, this.bannerFile, 'banner').toPromise()
      );
    }

    Promise.all(uploads)
      .then((results) => {
        this.saving = false;
        const last = results[results.length - 1];
        this.activeModal.close((last && last.data) ? last.data : true);
      })
      .catch((err) => {
        this.saving = false;
        this.saveError = (err && err.error && err.error.message) || 'Upload failed. Please try again.';
      });
  }
}
